import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { DataTablePaginationButton } from './DataTablePaginationButton'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { PaginationIcon, PaginationIconType } from '../../atoms/icons/PaginationIcon'
import { tID } from '../../utils/utils'

type DataTablePaginationProps = {
  currentPage: number
  totalPageCount: number
  onNextPage: () => void
  onPrevPage: () => void
  onFirstPage: () => void
  onLastPage: () => void
  onPageChange: (page: number) => void
  customStyle?: ViewStyle
}

const OuterContainer = styled.View({
  width: '100%',
  alignItems: 'center',
})

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
  padding: theme.spacing['8px'],
}))

const ButtonText = styled(BodyText)({
  userSelect: 'none',
})

const SIBLINGS = 1
const BOUNDARY = 1
const DOTS = 'dots'

function range(start: number, end: number) {
  const length = end - start + 1
  return Array.from({ length }, (_, index) => index + start)
}

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  currentPage,
  totalPageCount,
  onNextPage,
  onPrevPage,
  onFirstPage,
  onLastPage,
  onPageChange,
  customStyle,
}) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const paginationRange = useMemo((): (number | 'dots')[] => {
    const totalPageNumbers = SIBLINGS * 2 + 3 + BOUNDARY * 2
    if (totalPageNumbers >= totalPageCount) {
      return range(1, totalPageCount)
    }

    const leftSiblingIndex = Math.max(currentPage - SIBLINGS, BOUNDARY)
    const rightSiblingIndex = Math.min(currentPage + SIBLINGS, totalPageCount - BOUNDARY)

    const shouldShowLeftDots = leftSiblingIndex > BOUNDARY + 2
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - (BOUNDARY + 1)

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = SIBLINGS * 2 + BOUNDARY + 2
      return [...range(1, leftItemCount), DOTS, ...range(totalPageCount - (BOUNDARY - 1), totalPageCount)]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = BOUNDARY + 1 + 2 * SIBLINGS
      return [...range(1, BOUNDARY), DOTS, ...range(totalPageCount - rightItemCount, totalPageCount)]
    }

    return [
      ...range(1, BOUNDARY),
      DOTS,
      ...range(leftSiblingIndex, rightSiblingIndex),
      DOTS,
      ...range(totalPageCount - BOUNDARY + 1, totalPageCount),
    ]
  }, [totalPageCount, currentPage])

  const canGoBack = currentPage > 1
  const canGoForward = currentPage < totalPageCount
  return (
    <OuterContainer style={customStyle}>
      <Container>
        <DataTablePaginationButton
          onPress={onFirstPage}
          disabled={!canGoBack}
          accessibilityLabel={formatMessage({
            defaultMessage: 'First page',
            description: 'Button for first table page',
          })}
          testID={tID('DataTablePagination-first-page-button')}
        >
          <PaginationIcon
            type={PaginationIconType.FIRST}
            fillColor={canGoBack ? theme.colors.iconDefault : theme.colors.iconInactive}
          />
        </DataTablePaginationButton>
        <DataTablePaginationButton
          onPress={onPrevPage}
          disabled={!canGoBack}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Previous page',
            description: 'Button for previous table page',
          })}
          testID={tID('DataTablePagination-prev-page-button')}
        >
          <PaginationIcon
            type={PaginationIconType.PREV}
            fillColor={canGoBack ? theme.colors.iconDefault : theme.colors.iconInactive}
          />
        </DataTablePaginationButton>
        {paginationRange.map((pageNumber, index) => {
          const isActive = pageNumber === currentPage
          const isDots = pageNumber === DOTS
          return (
            <DataTablePaginationButton
              key={index}
              isActive={isActive}
              onPress={() => pageNumber !== DOTS && onPageChange(pageNumber)}
              disabled={isDots}
              accessibilityLabel={formatMessage(
                {
                  defaultMessage: 'Go to page {pageNumber}',
                  description: 'Button for table page',
                },
                { pageNumber },
              )}
              testID={tID(`DataTablePagination-page-${pageNumber}`)}
            >
              <ButtonText
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                text={isDots ? '...' : pageNumber.toString()}
                color={isActive ? theme.colors.textDarkBackground : theme.colors.textPrimary}
              />
            </DataTablePaginationButton>
          )
        })}
        <DataTablePaginationButton
          onPress={onNextPage}
          disabled={!canGoForward}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Next page',
            description: 'Button for next table page',
          })}
          testID={tID('DataTablePagination-next-page-button')}
        >
          <PaginationIcon
            type={PaginationIconType.NEXT}
            fillColor={canGoForward ? theme.colors.iconDefault : theme.colors.iconInactive}
          />
        </DataTablePaginationButton>
        <DataTablePaginationButton
          onPress={onLastPage}
          disabled={!canGoForward}
          accessibilityLabel={formatMessage({
            defaultMessage: 'Last page',
            description: 'Button for last table page',
          })}
          testID={tID('DataTablePagination-last-page-button')}
        >
          <PaginationIcon
            type={PaginationIconType.LAST}
            fillColor={canGoForward ? theme.colors.iconDefault : theme.colors.iconInactive}
          />
        </DataTablePaginationButton>
      </Container>
    </OuterContainer>
  )
}
